import { IF, IS_DEFINED, NO, UIColor, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHAutocompleteTextField } from "./RHAutocompleteTextField"
import { RHButton } from "./RHButton"
import { RHDatePicker } from "./RHDatePicker"
import { RHTextField } from "./RHTextField"
import { RHButtons, RHColors, RHFont, RHShadow, RHSpacing } from "./Utils_slop/RHDesignTokens"
import { RHFilterPanelTokens } from "./Utils_slop/RHViewTokens"


// ─── Public model ────────────────────────────────────────────────────────────
//
// The panel is pure UI: it produces a serializable `RHFilterCriteria` and emits it
// via `filterCriteriaDidChange`. It knows nothing about the data it filters, the
// data view, or pagination. The owning view controller decides how to APPLY the
// criteria — today client-side over a full in-memory list, later by sending the
// SAME criteria object to the server alongside a page request. This criteria/apply
// split is what keeps filtering safe once server pagination is introduced.

export interface RHFilterFieldDescriptor {
    key: string                                 // criteria key, e.g. "estimatedCost"
    label: string                               // row label, e.g. "Eeldatav maksumus"
    kind: "numberRange" | "dateRange"
    allowDecimals?: boolean                     // numberRange only (€ = YES, counts = NO)
    fromPlaceholder?: string                    // defaults to "Alates"
    toPlaceholder?: string                      // defaults to "Kuni"
}

export interface RHFilterTextFieldDescriptor {
    key: string
    label: string
    kind: "text"
    autocompleteStringsProvider?: () => string[]
}

export interface RHTextCriterion {
    text: string
}

export interface RHRangeCriterion {
    min?: number
    max?: number
}

export interface RHDateRangeCriterion {
    from?: Date
    to?: Date
}

export type RHFilterCriterion = RHRangeCriterion | RHDateRangeCriterion | RHTextCriterion
export type RHFilterCriteria = Record<string, RHFilterCriterion>


// ─── Toggle row ("⌕ Filtrid ▾") ──────────────────────────────────────────────
// A self-contained clickable surface (icon + label + chevron). Built as a UIView
// rather than an RHButton so the funnel SVG renders cleanly and the chevron can be
// flipped without fighting the button's automatic title font-sizing.

class RHFilterPanelToggleView extends UIView {

    readonly iconLabel = new UITextView(this.elementID + "Icon", UITextView.type.span)
        .configuredWithObject({
            text: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="vertical-align:-2px"><path d="M3 5h18l-7.2 8.2v4.9L10.2 20v-6.8L3 5z"/></svg>`,
            backgroundColor: UIColor.transparentColor,
            style: { color: RHColors.textPrimary.stringValue, pointerEvents: "none" }
        }).addedAsSubviewToView(this)

    readonly textLabel = new UITextView(this.elementID + "Label", UITextView.type.span)
        .configuredWithObject({
            text: "Filtrid",
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColors.textPrimary.stringValue,
                fontSize: RHFont.sizeSmall,
                fontWeight: "600",
                pointerEvents: "none"
            }
        }).addedAsSubviewToView(this)

    readonly chevronLabel = new UITextView(this.elementID + "Chevron", UITextView.type.span)
        .configuredWithObject({
            text: RHFilterPanelTokens.chevronCollapsedGlyph,
            backgroundColor: UIColor.transparentColor,
            style: {
                color: RHColors.textMuted.stringValue,
                fontSize: RHFont.sizeSmall,
                pointerEvents: "none"
            }
        }).addedAsSubviewToView(this)


    constructor(elementID?: string) {
        super(elementID);
        (this as RHFilterPanelToggleView).configureWithObject({
            backgroundColor: RHColors.white,
            style: {
                border: `1px solid ${RHColors.borderLight.stringValue}`,
                borderRadius: RHFilterPanelTokens.panelBorderRadius,
                cursor: "pointer",
                userSelect: "none"
            }
        })
    }


    updateChevronForExpansionState(isExpanded: boolean) {
        let chevronGlyph = RHFilterPanelTokens.chevronCollapsedGlyph
        if (isExpanded) {
            chevronGlyph = "▴"
        }
        this.chevronLabel.text = chevronGlyph
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHFilterPanelTokens
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        // icon | label (fills) | chevron, all vertically centred
        bounds.rectangleWithWidth(RHButtons.compactHeight * 0.5, 0).assignedAsFrameOfView(this.iconLabel)
        bounds.rectangleWithWidth(RHButtons.compactHeight * 0.5, 1).assignedAsFrameOfView(this.chevronLabel)
        bounds
            .rectangleByAddingX(RHButtons.compactHeight * 0.5 + RHSpacing.small)
            .rectangleByAddingWidth(-(RHButtons.compactHeight + RHSpacing.small * 2))
            .assignedAsFrameOfView(this.textLabel)
    }

}


// ─── A single filter row ─────────────────────────────────────────────────────
// label | from-input | "–" | to-input. The two inputs are RHTextField (numberRange)
// or RHDatePicker (dateRange) depending on the descriptor.

class RHFilterRangeRowView extends UIView {

    readonly key: string
    readonly _kind: "numberRange" | "dateRange"

    readonly labelView: UITextView
    readonly separatorView: UITextView

    readonly _fromNumberField?: RHTextField
    readonly _toNumberField?: RHTextField
    readonly _fromDatePicker?: RHDatePicker
    readonly _toDatePicker?: RHDatePicker

    // Notifies the panel that an input value changed.
    valueDidChange?: () => void


    constructor(descriptor: RHFilterFieldDescriptor, elementID?: string) {
        super(elementID)
        this.key = descriptor.key
        this._kind = descriptor.kind

        this.labelView = new UITextView(this.elementID + "Label", UITextView.type.span)
            .configuredWithObject({
                text: descriptor.label,
                textColor: RHColors.textPrimary,
                backgroundColor: UIColor.transparentColor,
                style: { fontSize: RHFont.sizeSmall }
            }).addedAsSubviewToView(this)

        this.separatorView = new UITextView(this.elementID + "Separator", UITextView.type.span)
            .configuredWithObject({
                text: "–",
                textAlignment: UITextView.textAlignment.center,
                textColor: RHColors.textMuted,
                backgroundColor: UIColor.transparentColor
            }).addedAsSubviewToView(this)

        const fromPlaceholder = descriptor.fromPlaceholder ?? "Alates"
        const toPlaceholder = descriptor.toPlaceholder ?? "Kuni"

        if (this._kind == "numberRange") {
            this._fromNumberField = this._makeNumberField(descriptor, fromPlaceholder)
            this._toNumberField = this._makeNumberField(descriptor, toPlaceholder)
        }
        else {
            this._fromDatePicker = this._makeDatePicker(fromPlaceholder)
            this._toDatePicker = this._makeDatePicker(toPlaceholder)
        }
    }


    _makeNumberField(descriptor: RHFilterFieldDescriptor, placeholder: string): RHTextField {
        const field = new RHTextField()
            .configuredWithObject({
                type: "number",
                minValue: 0,
                allowDecimals: descriptor.allowDecimals ?? NO,
                placeholderText: placeholder
            }).addedAsSubviewToView(this)
        field.controlEventTargetAccumulator.TextChange = () => {
            this.valueDidChange?.()
        }
        return field
    }


    _makeDatePicker(placeholder: string): RHDatePicker {
        const picker = new RHDatePicker()
            .configuredWithObject({ type: "date", placeholder: placeholder })
            .addedAsSubviewToView(this)
        picker.addTargetForControlEvent(RHDatePicker.controlEvent.DateChange, () => {
            this.valueDidChange?.()
        })
        return picker
    }


    _fromControl(): UIView {
        return this._fromNumberField ?? this._fromDatePicker!
    }

    _toControl(): UIView {
        return this._toNumberField ?? this._toDatePicker!
    }


    // Returns real `undefined` (NOT uicore `nil`, which is a Proxy sentinel that reads
    // as IS_DEFINED===true and coerces to 0 in comparisons) so an empty field genuinely
    // means "no bound" — otherwise a blank input would behave as "max 0".
    _numberFieldValue(field: RHTextField): number | undefined {
        if (field.text.trim().length == 0) {
            return undefined
        }
        const value = field.numericValue
        if (isNaN(value)) {
            return undefined
        }
        return value
    }


    criterion(): RHFilterCriterion {
        if (this._kind == "numberRange") {
            return {
                min: this._numberFieldValue(this._fromNumberField!),
                max: this._numberFieldValue(this._toNumberField!)
            }
        }
        return {
            from: this._fromDatePicker!.selectedDate,
            to: this._toDatePicker!.selectedDate
        }
    }


    hasValue(): boolean {
        const criterion = this.criterion() as RHRangeCriterion & RHDateRangeCriterion
        return IS_DEFINED(criterion.min) || IS_DEFINED(criterion.max)
            || IS_DEFINED(criterion.from) || IS_DEFINED(criterion.to)
    }


    clearInputs() {
        if (this._kind == "numberRange") {
            this._fromNumberField!.text = ""
            this._toNumberField!.text = ""
            return
        }
        this._fromDatePicker!.clear()
        this._toDatePicker!.clear()
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHFilterPanelTokens
        const bounds = this.contentBoundsWithInset(0)
        const rangeWidth = tokens.rangeFieldWidth * 2 + RHSpacing.small * 2 + tokens.rangeSeparatorWidth
        const minimumRangeWidth = tokens.minimumRangeFieldWidth * 2 + RHSpacing.small * 2 + tokens.rangeSeparatorWidth
        const fieldWidth = Math.min(tokens.rangeFieldWidth,
            (bounds.width - RHSpacing.small * 2 - tokens.rangeSeparatorWidth) * 0.5)
        this.separatorView.hidden = bounds.width < minimumRangeWidth

        bounds
            .IF(bounds.width >= tokens.labelWidth + RHSpacing.medium + rangeWidth)
                .rectangleWithHeight(RHButtons.compactHeight)
                .rectangleWithWidth(tokens.labelWidth)
                .assignedAsFrameOfView(this.labelView)
                .rectangleForNextColumn(RHSpacing.medium, tokens.rangeFieldWidth)
                .assignedAsFrameOfView(this._fromControl())
                .rectangleForNextColumn(RHSpacing.small, tokens.rangeSeparatorWidth)
                .assignedAsFrameOfView(this.separatorView)
                .rectangleForNextColumn(RHSpacing.small, tokens.rangeFieldWidth)
                .assignedAsFrameOfView(this._toControl())
            .ELSE()
                .rectangleWithHeight(this.labelView)
                .assignedAsFrameOfView(this.labelView)
                .rectangleForNextRow(RHSpacing.tiny, RHButtons.compactHeight)
                .IF(bounds.width < minimumRangeWidth)
                    .assignedAsFrameOfView(this._fromControl())
                    .rectangleForNextRow(RHSpacing.small, RHButtons.compactHeight)
                    .assignedAsFrameOfView(this._toControl())
                .ELSE()
                    .rectangleWithWidth(fieldWidth)
                    .assignedAsFrameOfView(this._fromControl())
                    .rectangleForNextColumn(RHSpacing.small, tokens.rangeSeparatorWidth)
                    .assignedAsFrameOfView(this.separatorView)
                    .rectangleForNextColumn(RHSpacing.small, fieldWidth)
                    .assignedAsFrameOfView(this._toControl())
                .ENDIF()
            .ENDIF()
    }

}


class RHFilterTextRowView extends UIView {

    readonly key: string
    readonly labelView: UITextView
    readonly textField = new RHAutocompleteTextField().configuredWithObject({
        strictSelection: NO,
        validatesAgainstAutocomplete: NO
    }).addedAsSubviewToView(this)
    valueDidChange?: () => void

    constructor(descriptor: RHFilterTextFieldDescriptor) {
        super()
        this.key = descriptor.key
        this.labelView = new UITextView(this.elementID + "Label", UITextView.type.span)
            .configuredWithObject({
                text: descriptor.label,
                textColor: RHColors.textPrimary,
                backgroundColor: UIColor.transparentColor,
                style: { fontSize: RHFont.sizeSmall }
            }).addedAsSubviewToView(this)
        this.textField.textElementView.viewHTMLElement.setAttribute("aria-label", descriptor.label)
        this.textField.controlEventTargetAccumulator.Focus.TextChange = () => {
            // Typing must not commit a suggestion on Enter/Tab, including under a stationary pointer.
            this.textField.configureWithObject({
                autocompleteStrings: [...new Set(
                    (descriptor.autocompleteStringsProvider?.() ?? []).filter(text => text.trim().length > 0)
                )],
                _userHasNavigatedDropdown: NO,
                _dropdownView: { highlightedRowIndex: -1, _suppressHoverHighlight: YES }
            })
        }
        this.textField.controlEventTargetAccumulator.TextChange = () => {
            this.valueDidChange?.()
        }
        this.textField.controlEventTargetAccumulator.SelectionDidChange.EscDown = () => {
            this.valueDidChange?.()
        }
    }

    criterion(): RHTextCriterion {
        return { text: this.textField.text.trim() }
    }

    hasValue(): boolean {
        return this.criterion().text.length > 0
    }

    clearInputs() {
        this.textField.configureWithObject({ text: "", _selectedItem: undefined })
        this.textField.closeDropdown()
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHFilterPanelTokens
        const bounds = this.contentBoundsWithInset(0)
        const rangeWidth = tokens.rangeFieldWidth * 2 + RHSpacing.small * 2 + tokens.rangeSeparatorWidth

        bounds
            .IF(bounds.width >= tokens.labelWidth + RHSpacing.medium + rangeWidth)
                .rectangleWithHeight(RHButtons.compactHeight)
                .rectangleWithWidth(tokens.labelWidth)
                .assignedAsFrameOfView(this.labelView)
                .rectangleForNextColumn(RHSpacing.medium, rangeWidth)
                .assignedAsFrameOfView(this.textField)
            .ELSE()
                .rectangleWithHeight(this.labelView)
                .assignedAsFrameOfView(this.labelView)
                .rectangleForNextRow(RHSpacing.tiny, RHButtons.compactHeight)
                .assignedAsFrameOfView(this.textField)
            .ENDIF()
    }

}


// ─── The collapsible panel body ──────────────────────────────────────────────
// A styled surface holding the stacked rows plus a "Tühjenda / Sulge" footer.

class RHFilterPanelBodyView extends UIView {

    readonly clearButton = new RHButton()
        .configuredWithObject({
            variant: "softWarning",
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            titleLabel: { text: "Tühjenda" }
        }).addedAsSubviewToView(this)

    readonly closeButton = new RHButton()
        .configuredWithObject({
            variant: "softDanger",
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            titleLabel: { text: "Sulge" }
        }).addedAsSubviewToView(this)

    _rows: (RHFilterRangeRowView | RHFilterTextRowView)[] = []


    constructor(elementID?: string) {
        super(elementID);
        (this as RHFilterPanelBodyView).configureWithObject({
            backgroundColor: RHColors.white,
            style: {
                border: `1px solid ${RHColors.borderLight.stringValue}`,
                borderRadius: RHFilterPanelTokens.panelBorderRadius,
                boxShadow: RHShadow.subtle,
                boxSizing: "border-box",
                overflow: "hidden"
            }
        })
    }


    // Preserve the existing panel API while accepting text rows.
    setRows(rows: (RHFilterRangeRowView | RHFilterTextRowView)[]) { // project-rules-ignore no-java-accessor-naming
        this._rows.forEach(row => row.removeFromSuperview())
        this._rows = rows
        rows.forEach(row => row.addedAsSubviewToView(this))
        this.clearButton.moveToTopOfSuperview()
        this.closeButton.moveToTopOfSuperview()
        this.setNeedsLayout()
    }


    override boundsDidChange(bounds: UIRectangle) {
        super.boundsDidChange(bounds)
        // Row measurements must reflect the new width before the next virtual pass.
        for (const row of this._rows) {
            if (row.frame.width !== bounds.width - RHSpacing.large * 2) {
                row.setNeedsLayout()
            }
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHFilterPanelTokens
        const bounds = this.contentBoundsWithInset(RHSpacing.large)

        bounds
            .framesByDistributingViewsAsColumn(this._rows, RHSpacing.small)
            .boundingBox()
            .rectangleForNextRow(RHSpacing.medium, tokens.actionButtonHeight)
            .framesByDistributingViewsAsRow([this.clearButton, this.closeButton],
                RHSpacing.small, [tokens.actionButtonWidth, tokens.actionButtonWidth], 1)
    }

    // The panel is only as wide as its content (label + two input columns + padding) —
    // it never stretches to the full table width.
    static panelWidth(): number {
        const tokens = RHFilterPanelTokens
        return RHSpacing.large * 2
            + tokens.labelWidth + RHSpacing.medium
            + tokens.rangeFieldWidth * 2 + RHSpacing.small * 2 + tokens.rangeSeparatorWidth
    }

}


// ─── The reusable filter panel ───────────────────────────────────────────────

export class RHFilterPanelView extends UIView {

    readonly toggleView = new RHFilterPanelToggleView(this.elementID + "Toggle")
        .addedAsSubviewToView(this)

    readonly bodyView = new RHFilterPanelBodyView(this.elementID + "Body")
        .addedAsSubviewToView(this)

    _fields: (RHFilterFieldDescriptor | RHFilterTextFieldDescriptor)[] = []
    _rows: (RHFilterRangeRowView | RHFilterTextRowView)[] = []
    _isExpanded = NO
    _suppressCallback = NO
    _usesExternalToggle = NO

    // Fired on every input change and on clear (never during a programmatic reset).
    filterCriteriaDidChange?: (criteria: RHFilterCriteria) => void

    // Fired whenever expansion changes (by the built-in toggle, the "Sulge" button,
    // or an external caller setting isExpanded). Lets an owner that drives the panel
    // with its own toggle button keep that button's pressed state in sync.
    isExpandedDidChange?: (isExpanded: boolean) => void


    constructor(elementID?: string) {
        super(elementID)
        this.bodyView.hidden = YES

        this.toggleView.controlEventTargetAccumulator.PointerUpInside = () => {
            this.isExpanded = NO == this._isExpanded
        }
        this.bodyView.clearButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.clearCriteria()
        }
        this.bodyView.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.isExpanded = NO
        }
    }


    // ── Public API ───────────────────────────────────────────────────────────

    get fields(): (RHFilterFieldDescriptor | RHFilterTextFieldDescriptor)[] {
        return this._fields
    }

    set fields(fields: (RHFilterFieldDescriptor | RHFilterTextFieldDescriptor)[]) {
        this._fields = fields
        this._rows = fields.map(descriptor => {
            const row = IF(descriptor.kind === "text")(() =>
                new RHFilterTextRowView(descriptor as RHFilterTextFieldDescriptor)
            ).ELSE(() => new RHFilterRangeRowView(descriptor as RHFilterFieldDescriptor))
            row.valueDidChange = () => this._emitCriteria()
            return row
        })
        this.bodyView.setRows(this._rows)
        this.setNeedsLayout()
    }


    get isExpanded(): boolean {
        return this._isExpanded
    }

    set isExpanded(isExpanded: boolean) {
        if (this._isExpanded == isExpanded) {
            return
        }
        const previousSuperview = this.superview
        this._isExpanded = isExpanded
        this.bodyView.hidden = NO == isExpanded
        this.toggleView.updateChevronForExpansionState(isExpanded)
        this.isExpandedDidChange?.(isExpanded)
        // The expansion callback may attach, detach, or move the panel.
        if (previousSuperview && previousSuperview !== this.superview) {
            previousSuperview.setNeedsLayoutUpToRootView()
        }
        this.setNeedsLayoutUpToRootView()
    }


    // When YES, the built-in "⌕ Filtrid ▾" toggle is hidden and the panel reserves
    // no space for it: the owner supplies its own toggle button and drives
    // isExpanded. Collapsed height becomes 0; expanded shows only the body.
    get usesExternalToggle(): boolean {
        return this._usesExternalToggle
    }

    set usesExternalToggle(usesExternalToggle: boolean) {
        this._usesExternalToggle = usesExternalToggle
        this.toggleView.hidden = usesExternalToggle
        this.setNeedsLayoutUpToRootView()
    }


    get criteria(): RHFilterCriteria {
        const result: RHFilterCriteria = {}
        this._rows.forEach(row => {
            if (row.hasValue()) {
                result[row.key] = row.criterion()
            }
        })
        return result
    }


    applyTextCriterionForKey(criterion: RHTextCriterion, key: string): void {
        const row = this._rows.find(row => row.key === key)
        if (!(row instanceof RHFilterTextRowView)) return
        row.clearInputs()
        row.textField.text = criterion.text
    }


    clearCriteria() {
        this._suppressCallback = YES
        this._rows.forEach(row => row.clearInputs())
        this._suppressCallback = NO
        this._emitCriteria()
    }


    // ── Pure client-side matchers (used by any owner to apply criteria) ────────

    static doesTextMatchCriterion(text: string, criterion?: RHTextCriterion): boolean {
        return text.toLowerCase().includes(criterion?.text.trim().toLowerCase() ?? "")
    }

    static matchesNumberRange(value: number, criterion?: RHRangeCriterion): boolean {
        if (!criterion) {
            return YES
        }
        if (IS_DEFINED(criterion.min) && value < criterion.min) {
            return NO
        }
        if (IS_DEFINED(criterion.max) && value > criterion.max) {
            return NO
        }
        return YES
    }

    static matchesDateRange(value: Date | undefined, criterion?: RHDateRangeCriterion): boolean {
        if (!criterion) {
            return YES
        }
        if (IS_DEFINED(criterion.from)) {
            if (!value || value < RHFilterPanelView._startOfDay(criterion.from)) {
                return NO
            }
        }
        if (IS_DEFINED(criterion.to)) {
            if (!value || value > RHFilterPanelView._endOfDay(criterion.to)) {
                return NO
            }
        }
        return YES
    }

    // The picked day is inclusive even though the data carries a time component.
    static _startOfDay(date: Date): Date {
        const result = new Date(date)
        result.setHours(0, 0, 0, 0)
        return result
    }

    static _endOfDay(date: Date): Date {
        const result = new Date(date)
        result.setHours(23, 59, 59, 999)
        return result
    }


    // ── Internal ───────────────────────────────────────────────────────────────

    _emitCriteria() {
        if (this._suppressCallback) {
            return
        }
        const criteria = this.criteria
        this.filterCriteriaDidChange?.(criteria)
    }


    override intrinsicContentHeight(constrainingWidth: number = 0): number {
        const bodyWidth = Math.min(constrainingWidth, RHFilterPanelBodyView.panelWidth())

        // External toggle: no built-in toggle row, so the panel is 0-tall when
        // collapsed and exactly the body when expanded.
        if (this._usesExternalToggle) {
            if (!this._isExpanded) {
                return 0
            }
            return this.bodyView.intrinsicContentHeight(bodyWidth)
        }

        let height = RHButtons.compactHeight
        if (this._isExpanded) {
            height += RHSpacing.small + this.bodyView.intrinsicContentHeight(bodyWidth)
        }
        return height
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)

        // External toggle: the owner's button lives elsewhere; lay out only the body.
        if (this._usesExternalToggle) {
            if (!this._isExpanded) {
                return
            }
            bounds
                .rectangleWithMaxWidth(RHFilterPanelBodyView.panelWidth(), 0)
                .rectangleWithHeight(this.bodyView)
                .assignedAsFrameOfView(this.bodyView)
            return
        }

        const toggleFrame = bounds
            .rectangleWithHeight(RHButtons.compactHeight)
            .rectangleWithWidth(150, 0)
        toggleFrame.assignedAsFrameOfView(this.toggleView)

        if (!this._isExpanded) {
            return
        }
        bounds
            .rectangleWithHeight(RHButtons.compactHeight)
            .rectangleForNextRow(RHSpacing.small, 0)
            .rectangleWithMaxWidth(RHFilterPanelBodyView.panelWidth(), 0)
            .rectangleWithHeight(this.bodyView)
            .assignedAsFrameOfView(this.bodyView)
    }

}
